import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { styled } from '@mui/material/styles';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view';
import { Typography, Stack, SvgIcon, Box, } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { CloudDownloadOutlined as CloudDownloadOutlinedIcon, PictureAsPdf as PdfIcon, CardMembership as CertificationIcon, } from '@mui/icons-material';
import { useTokens } from '@mcn-platform/models';
import { borderColorLight } from '../../tss/colors';
import { courseItemType, courseItemStatus } from '../../course/constants';
import capitalizeFirstLetter from '../../common/capitalizeFirstLetter';
import PlayCircleIcon from '../../icons/PlayCircleIcon';
import MinusCircleIcon from '../../icons/MinusCircleIcon';
import { CheckCircleSecondary } from '../../icons/CheckCircleIcon';
import CrossCircleIcon from '../../icons/CrossCircleIcon';
import DocumentCheckIcon from '../../icons/DocumentCheckIcon';
import PencilIcon from '../../icons/PencilIcon';
import DocumentIcon from '../../icons/DocumentIcon';
import VideoIcon from '../../icons/VideoIcon';
import AudioIcon from '../../icons/AudioIcon';
import MinusIcon from '../../icons/MinusIcon';
import PlusIcon from '../../icons/PlusIcon';
const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
    color: theme.palette.subNav.text.primary,
    marginBottom: 1,
    // 1st level
    [`& .${treeItemClasses.content}`]: {
        position: 'relative',
        minHeight: '45px',
        color: theme.palette.subNav.text.primary,
        borderRadius: theme.spacing(2),
        padding: theme.spacing(1),
        marginTop: theme.spacing(2),
        fontWeight: theme.typography.fontWeightBold,
        background: theme.portal?.site?.sidebar?.selectedItem?.backgroundColor,
        '&:hover': {
            backgroundColor: theme.portal?.site?.sidebar?.selectedItem?.backgroundColor,
        },
        '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
            backgroundColor: theme.portal?.site?.sidebar?.selectedItem?.backgroundColor,
        },
        [`& .${treeItemClasses.label}`]: {
            fontWeight: 'inherit',
            color: 'inherit',
        },
    },
    // 2nd level
    [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: 0,
        [`& .${treeItemClasses.content}`]: {
            // width: 'calc(100% - 24px)', // fix smooth collapse transition
            color: 'subNav.text.primary',
            background: 'transparent',
            paddingLeft: theme.spacing(2.5),
            [`& .${treeItemClasses.label}`]: {
                color: 'inherit',
                paddingLeft: theme.spacing(1.625),
            },
        },
    },
}));
const StyledTreeItem = ({ bgColor, color, section, isRoot, selectedItem, setSelectedItem, viewStatus, showLessonType, ...other }) => {
    const tokens = useTokens();
    // console.log('course item => ', section);
    const isItem = section.type !== 'container'; // section.item && Object.keys(section.item).length > 0;
    const size = '31px';
    if (isItem) {
        const { name, item } = section;
        const { type } = item;
        let courseItemStatusIcon = _jsx(PlayCircleIcon, { height: size, width: size, viewBox: "2 2 20 20" });
        let itemIcon;
        let itemInfo = '';
        switch (viewStatus) {
            case (courseItemStatus.NOT_STARTED):
                courseItemStatusIcon = _jsx(PlayCircleIcon, { height: size, width: size, viewBox: "2 2 20 20" });
                break;
            case (courseItemStatus.IN_PROGRESS):
                courseItemStatusIcon = _jsx(MinusCircleIcon, { width: size, height: size });
                break;
            case (courseItemStatus.DONE):
                courseItemStatusIcon = _jsx(CheckCircleSecondary, { width: size, height: size });
                break;
            case (courseItemStatus.SKIPPED):
                courseItemStatusIcon = _jsx(CrossCircleIcon, { width: size, height: size });
                break;
            default:
                console.log('unknown course item status.');
                break;
        }
        switch (type.toUpperCase()) {
            case (courseItemType.BLOCK):
                itemIcon = _jsx(DocumentIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            case (courseItemType.VIDEO):
            case (courseItemType.MICROCAST):
            case (courseItemType.SCORM):
                itemIcon = _jsx(VideoIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            case (courseItemType.AUDIO):
                itemIcon = _jsx(AudioIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            case (courseItemType.QUIZ):
                itemIcon = _jsx(PencilIcon, { width: "26px", height: "26px" });
                // TODO: here item info should student grade
                itemInfo = item.length;
                break;
            case (courseItemType.ARTICLE):
                itemIcon = _jsx(DocumentCheckIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            case (courseItemType.PDF):
                itemIcon = _jsx(PdfIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            case (courseItemType.DOWNLOAD):
                itemIcon = _jsx(SvgIcon, { sx: { fontSize: '28px !important' }, children: _jsx(CloudDownloadOutlinedIcon, {}) });
                itemInfo = item.length;
                break;
            case (courseItemType.KNOWLEDGECHECK):
                itemIcon = _jsx(PencilIcon, { width: "26px", height: "26px" });
                itemInfo = item.length;
                break;
            case (courseItemType.CERTIFICATION):
                itemIcon = _jsx(CertificationIcon, { width: "30px", height: "30px" });
                itemInfo = item.length;
                break;
            case (courseItemType.TYPEFORM):
                itemIcon = _jsx(DocumentCheckIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            case (courseItemType.HTML):
                itemIcon = _jsx(DocumentIcon, { width: size, height: size });
                itemInfo = item.length;
                break;
            default:
                console.log('unknown course item type.');
                break;
        }
        const itemElement = () => (_jsx(Box, { sx: { color: (t) => t.portal?.courseDeliveryPage?.courseOutlineIcon?.color }, children: itemIcon }));
        courseItemStatusIcon = (_jsx(Box, { sx: { color: (t) => t.portal?.courseDeliveryPage?.courseOutlineButton?.color }, children: courseItemStatusIcon }));
        const itemTypeTokenMap = {
            audio: 'audioButtonLabel',
            article: 'articleButtonLabel',
            block: 'blockButtonLabel',
            certification: 'certificationButtonLabel',
            course: 'courseButtonLabel',
            download: 'downloadButtonLabel',
            knowledgeCheck: 'knowledgeCheckButtonLabel',
            microcast: 'microcastButtonLabel',
            pdf: 'pdfButtonLabel',
            quiz: 'quizButtonLabel',
            scorm: 'scormButtonLabel',
            video: 'videoButtonLabel',
            typeform: 'typeformButtonLabel',
            html: 'htmlButtonLabel',
        };
        const getItemTitle = (itemType, itemName, shouldShowLessonType) => {
            const tokenKey = itemTypeTokenMap[itemType];
            const typeLabel = tokens.getToken(tokenKey);
            return shouldShowLessonType ? `${typeLabel}: ${itemName}` : itemName;
        };
        // console.log('name:', name, isRoot);
        const itemTitle = getItemTitle(type, name, showLessonType);
        return (_jsx(StyledTreeItemRoot
        // eslint-disable-next-line react/jsx-props-no-spreading
        , { ...other, slots: { icon: itemElement }, label: (_jsxs(Stack, { direction: "row", justifyContent: "space-between", children: [_jsxs(Stack, { justifyContent: "center", children: [_jsx(Typography, { variant: "body1", className: "courseOutlineText", children: itemTitle }), _jsx(Typography, { variant: "body1", sx: { color: 'info.main' }, children: itemInfo })] }), _jsx(Stack, { my: "auto", direction: "row", spacing: 0.625, color: "primary.light", children: courseItemStatusIcon })] })), sx: {
                [`& .${treeItemClasses.content}`]: {
                    backgroundColor: 'transparent',
                    borderRadius: '0 !important',
                    borderBottom: `1px solid ${borderColorLight}`,
                    [`& .${treeItemClasses.iconContainer}`]: {
                        width: 33,
                        marginLeft: -1,
                    },
                    '&:hover': {
                        backgroundColor: (t) => (t.portal.site.sidebar.selectedItem.backgroundColor),
                    },
                },
                ...(selectedItem?.id === section.id && {
                    [`& .${treeItemClasses.content}`]: {
                        backgroundColor: (t) => (t.portal.site.sidebar.selectedItem.backgroundColor),
                        '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
                            backgroundColor: (t) => (t.portal.site.sidebar.selectedItem.backgroundColor),
                        },
                    },
                    '.MuiTreeItem-iconContainer': {
                        width: '33px !important',
                    },
                }),
            }, onClick: () => setSelectedItem(section) }));
    }
    return (_jsx(StyledTreeItemRoot
    // eslint-disable-next-line react/jsx-props-no-spreading
    , { ...other, label: isRoot
            ? (_jsx("div", { children: _jsx(Typography, { variant: "subtitle1", className: "courseOutlineText", children: capitalizeFirstLetter(section.name) }) }))
            : (_jsx("div", { children: _jsx(Typography, { variant: "subtitle2", className: "courseOutlineText", children: capitalizeFirstLetter(section.name) }) })), slots: {
            collapseIcon: isRoot
                ? MinusIcon
                : ArrowDownwardIcon,
            expandIcon: isRoot ? PlusIcon : ArrowForwardIcon,
        }, sx: {
            [`& .${treeItemClasses.content}`]: {
                [`& .${treeItemClasses.iconContainer}`]: {
                    // width: 31,
                    marginRight: 0,
                },
                [`& .${treeItemClasses.label}`]: {
                    paddingLeft: 0,
                },
            },
        } }));
};
StyledTreeItem.defaultProps = {
    bgColor: '',
    color: '',
};
export default StyledTreeItem;
//# sourceMappingURL=StyledTreeItem.js.map